import React, { useEffect, useState } from "react";
import { Modal, Form, Row, Col, Spinner } from "react-bootstrap";
import { Lightning, CheckCircle } from "react-bootstrap-icons";
import { WithApiAccess } from "../../../../lib/HigherOrderComponents/WithApiAccess";
import "../../styles/EnhancementModal.css";

const CHARACTER_LIMIT = 2000;

function EnhancementModalInner({ show, onHide, api, onSubmitted }) {
  const username = localStorage.getItem("username") || "";

  const [details, setDetails] = useState("");
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Reset state every time the modal opens fresh
  useEffect(() => {
    if (show) {
      setDetails("");
      setTouched(false);
      setIsSubmitting(false);
      setSubmitted(false);
      setError(null);
    }
  }, [show]);

  const isValid = details.trim().length > 0;
  const showRequiredError = touched && !isValid;
  const isAtLimit = details.length >= CHARACTER_LIMIT;
  const counterColor = isAtLimit ? "var(--cp-danger)" : "var(--cp-text-muted)";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (!isValid) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.post("api/enhancement-request", {
        username,
        details,
      });

      if (response.status === 200) {
        setSubmitted(true);
        if (onSubmitted) onSubmitted();
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Enhancement submit failed", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="cp-enhance-modal"
      contentClassName="cp-enhance-modal__content"
    >
      <Modal.Header closeButton className="cp-enhance-modal__header">
        <Row className="g-0 align-items-center w-100">
          <Col xs="auto">
            <div className="cp-enhance-modal__icon">
              <Lightning />
            </div>
          </Col>
          <Col className="ps-3">
            <Modal.Title className="cp-enhance-modal__title">
              Enhancement Request
            </Modal.Title>
            <p className="cp-enhance-modal__subtitle">
              Share your ideas to help us improve
            </p>
          </Col>
        </Row>
      </Modal.Header>

      <Modal.Body className="cp-enhance-modal__body">
        {submitted ? (
          <SuccessView onClose={onHide} />
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="cp-enhance-modal__label">
                    Username
                  </Form.Label>
                  <div className="cp-enhance-modal__readonly">{username}</div>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="cp-enhance-modal__label">
                    Describe your request{" "}
                    <span className="cp-enhance-modal__required">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    maxLength={CHARACTER_LIMIT}
                    placeholder="What would you like to see improved?"
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    onBlur={() => setTouched(true)}
                    isInvalid={showRequiredError}
                    className="cp-enhance-modal__control cp-enhance-modal__textarea"
                    disabled={isSubmitting}
                  />
                  <Row className="g-0 cp-enhance-modal__meta">
                    <Col>
                      {showRequiredError && (
                        <span className="cp-enhance-modal__error">
                          Please describe your request
                        </span>
                      )}
                      {error && !showRequiredError && (
                        <span className="cp-enhance-modal__error">{error}</span>
                      )}
                    </Col>
                    <Col xs="auto">
                      <span
                        className="cp-enhance-modal__count"
                        style={{ color: counterColor }}
                      >
                        {details.length} / {CHARACTER_LIMIT}
                      </span>
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-2 justify-content-end cp-enhance-modal__actions">
              <Col xs="auto">
                <button
                  type="button"
                  className="cp-enhance-modal__secondary-btn"
                  onClick={onHide}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </Col>
              <Col xs="auto">
                <button
                  type="submit"
                  className="cp-enhance-modal__primary-btn"
                  disabled={isSubmitting || !isValid}
                >
                  {isSubmitting ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

function SuccessView({ onClose }) {
  return (
    <Row className="g-0 justify-content-center text-center cp-enhance-modal__success">
      <Col xs={12}>
        <div className="cp-enhance-modal__success-icon">
          <CheckCircle />
        </div>
        <h3 className="cp-enhance-modal__success-title">Request Submitted</h3>
        <p className="cp-enhance-modal__success-text">
          Thank you! Your enhancement request has been received and will be
          reviewed by our team.
        </p>
        <button
          type="button"
          className="cp-enhance-modal__primary-btn"
          onClick={onClose}
        >
          Done
        </button>
      </Col>
    </Row>
  );
}

export const EnhancementModal = WithApiAccess(EnhancementModalInner);
